import { useState, useMemo, useCallback } from "react";
import { NormalizedRelease } from "@/types/sofa";
import { Device } from "@/types/device";
import { Vulnerability } from "@/types/vulnerability";
import { useNvdEnrichment } from "@/hooks/useNvdEnrichment";
import { enrichReleasesWithFleetData, getFleetExposure, generateRecommendedActions, getImpactedDevices } from "@/utils/vulnerabilityMatching";
import { RecommendedActions } from "./RecommendedActions";
import { VulnerabilityFilters } from "./VulnerabilityFilters";
import { VulnerabilityTable } from "./VulnerabilityTable";
import { VulnerabilityDetailPanel } from "./VulnerabilityDetailPanel";
import { FleetExposureChart } from "./FleetExposureChart";
import { ReleaseTimelineChart } from "./ReleaseTimelineChart";
import { PlatformBreakdownChart } from "./PlatformBreakdownChart";
import { GlassCard, GlassCardContent, GlassCardHeader, GlassCardTitle } from "@/components/ui/glass-card";
import { Shield, AlertTriangle, Bug, Clock, Monitor, RefreshCw, Info, ShieldCheck, ShieldX, ChevronDown as ChevronDownIcon } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { format, formatDistanceToNow } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useCompactMode } from "@/hooks/useCompactMode";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible as CollapsibleRoot, CollapsibleContent as CollapsibleBody, CollapsibleTrigger as CollapsibleHead } from "@/components/ui/collapsible";
import { ApplicationsVulnerabilityTab } from "./ApplicationsVulnerabilityTab";
import { RemediationPolicyTab } from "./RemediationPolicyTab";

interface VulnerabilityDashboardProps {
  releases: NormalizedRelease[];
  totalCVEs: number;
  activelyExploitedCount: number;
  lastCheck?: string;
  devices: Device[];
  onRefresh?: () => void;
  isRefreshing?: boolean;
  loadedCount?: number;
  totalCount?: number;
  cacheStatus?: "fresh" | "cached" | "fetching";
  lastDataUpdate?: number;
}

export function VulnerabilityDashboard({
  releases,
  totalCVEs,
  activelyExploitedCount,
  lastCheck,
  devices,
  onRefresh,
  isRefreshing,
  loadedCount,
  totalCount,
  cacheStatus,
  lastDataUpdate,
}: VulnerabilityDashboardProps) {
  const [timeRange, setTimeRange] = useState("all");
  const [software, setSoftware] = useState("all");
  const [severity, setSeverity] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [showOnlyRelevant, setShowOnlyRelevant] = useState(false);
  const [selectedVulnerability, setSelectedVulnerability] = useState<Vulnerability | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const { cx } = useCompactMode();

  const allCveIds = useMemo(
    () => [...new Set(releases.flatMap(r => r.cves.map(c => c.id)))],
    [releases]
  );
  const { data: nvdData } = useNvdEnrichment(allCveIds);

  const enrichedReleases = useMemo(
    () => enrichReleasesWithFleetData(releases, devices),
    [releases, devices]
  );

  const fleetExposure = useMemo(
    () => getFleetExposure(releases, devices),
    [releases, devices]
  );

  const recommendedActions = useMemo(
    () => generateRecommendedActions(releases, devices),
    [releases, devices]
  );

  const urgentDeviceCount = useMemo(
    () => recommendedActions.filter(a => a.urgency === "critical").reduce((sum, a) => sum + a.affectedDevices.length, 0),
    [recommendedActions]
  );

  const filteredReleases = useMemo(() => {
    return enrichedReleases.filter(release => {
      if (timeRange !== "all") {
        const releaseDate = new Date(release.releaseDate);
        const now = new Date();
        const daysDiff = (now.getTime() - releaseDate.getTime()) / (1000 * 60 * 60 * 24);
        if (timeRange === "24h" && daysDiff > 1) return false;
        if (timeRange === "7d" && daysDiff > 7) return false;
        if (timeRange === "30d" && daysDiff > 30) return false;
        if (timeRange === "90d" && daysDiff > 90) return false;
      }
      if (software !== "all") {
        const name = typeof release.productName === "string" ? release.productName.toLowerCase() : "";
        const sw = software.toLowerCase();
        if (!name.includes(sw)) return false;
      }
      if (severity !== "all") {
        const pr = typeof release.priority === "string" ? release.priority.toLowerCase() : "";
        if (pr !== severity.toLowerCase()) return false;
      }
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const updateName = typeof release.updateName === "string" ? release.updateName.toLowerCase() : "";
        const productVersion = typeof release.productVersion === "string" ? release.productVersion.toLowerCase() : "";
        const cves = Array.isArray(release.cves) ? release.cves : [];
        const matchesName = updateName.includes(query);
        const matchesCVE = cves.some(c => typeof c?.id === "string" && c.id.toLowerCase().includes(query));
        const matchesVersion = productVersion.includes(query);
        if (!matchesName && !matchesCVE && !matchesVersion) return false;
      }
      if (showOnlyRelevant) {
        if ((release.impactedDeviceCount || 0) === 0) return false;
      }
      return true;
    });
  }, [enrichedReleases, timeRange, software, severity, searchQuery, showOnlyRelevant]);

  const handleCVEClick = (cveId: string, release: NormalizedRelease) => {
    const cveDetail = release.cves.find(c => c.id === cveId);
    if (!cveDetail) return;

    const nvdCve = nvdData?.get(cveId);
    const impactedDevices = getImpactedDevices(release, devices);

    const vuln: Vulnerability = {
      cve_id: cveId,
      severity: nvdCve
        ? nvdCve.severity
        : (cveDetail.detail.severity?.toUpperCase() || "MEDIUM") as any,
      cvss_score: nvdCve?.cvssScore ?? (
        cveDetail.detail.severity === "Critical" ? 9.5
        : cveDetail.detail.severity === "High" ? 7.5
        : 5.0
      ),
      description: nvdCve?.description || `This CVE was addressed in ${release.updateName}. ${release.summary}`,
      actively_exploited: cveDetail.detail.actively_exploited || false,
      software_names: [release.updateName],
      os_types: [release.productName],
      published_date: nvdCve?.publishedDate || release.releaseDate,
      affected_updates: [{
        name: release.updateName,
        version: release.productVersion,
        os_type: release.productName,
        released: release.releaseDate,
        notes_url: release.securityInfo,
      }],
      impacted_devices: impactedDevices,
    };

    setSelectedVulnerability(vuln);
    setDetailPanelOpen(true);
  };

  const totalReleasesWithCVEs = enrichedReleases.filter(r => r.cveCount > 0).length;
  const criticalReleases = enrichedReleases.filter(r => r.priority === "critical").length;

  return (
    <div className={cx("space-y-6", "space-y-4")}>
      {/* Last updated */}
      {lastCheck && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          Feed updated {formatDistanceToNow(new Date(lastCheck), { addSuffix: true })}
          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="ml-1 p-1 rounded-md hover:bg-muted transition-colors disabled:opacity-50"
              title="Refresh feeds"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
            </button>
          )}
          {loadedCount != null && totalCount != null && loadedCount < totalCount && (
            <span className="ml-2 text-xs text-muted-foreground/70 animate-pulse">
              Loading feeds ({loadedCount}/{totalCount})…
            </span>
          )}
          {cacheStatus && (
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className={`ml-2 inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border cursor-help ${
                    cacheStatus === "fresh"
                      ? "bg-success/10 text-success border-success/20"
                      : cacheStatus === "fetching"
                        ? "bg-primary/10 text-primary border-primary/20"
                        : "bg-warning/10 text-warning border-warning/20"
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${
                      cacheStatus === "fresh"
                        ? "bg-success"
                        : cacheStatus === "fetching"
                          ? "bg-primary animate-pulse"
                          : "bg-warning"
                    }`} />
                    {cacheStatus === "fresh" ? "Live" : cacheStatus === "fetching" ? "Updating…" : "Cached"}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-[220px] text-center">
                  {cacheStatus === "fresh" && "Data is up-to-date — fetched directly from vulnerability feeds."}
                  {cacheStatus === "cached" && "Showing previously saved data. Feeds auto-refresh every 30 minutes."}
                  {cacheStatus === "fetching" && "Fetching the latest data from vulnerability feeds…"}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          {cacheStatus === "cached" && lastDataUpdate && lastDataUpdate > 0 && (
            <span className="text-xs text-muted-foreground/60">
              · fetched {formatDistanceToNow(new Date(lastDataUpdate), { addSuffix: true })}
            </span>
          )}
        </div>
      )}

      {/* Action Banner */}
      {urgentDeviceCount > 0 ? (
        <div className={cx("rounded-2xl border border-destructive/30 bg-destructive/5 p-5 flex items-start gap-4", "rounded-xl border border-destructive/30 bg-destructive/5 p-3 flex items-start gap-3")}>
          <div className={cx("p-3 rounded-xl bg-destructive/10 border border-destructive/20", "p-2 rounded-lg bg-destructive/10 border border-destructive/20")}>
            <ShieldX className={cx("h-6 w-6 text-destructive", "h-5 w-5 text-destructive")} />
          </div>
          <div>
            <h2 className={cx("text-lg font-bold", "text-base font-bold")}>{urgentDeviceCount} device{urgentDeviceCount !== 1 ? "s" : ""} need immediate updates</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              Actively exploited vulnerabilities affect your fleet. Review the recommended actions below to prioritize remediation.
            </p>
          </div>
        </div>
      ) : (
        <div className={cx("rounded-2xl border border-success/30 bg-success/5 p-5 flex items-start gap-4", "rounded-xl border border-success/30 bg-success/5 p-3 flex items-start gap-3")}>
          <div className={cx("p-3 rounded-xl bg-success/10 border border-success/20", "p-2 rounded-lg bg-success/10 border border-success/20")}>
            <ShieldCheck className={cx("h-6 w-6 text-success", "h-5 w-5 text-success")} />
          </div>
          <div>
            <h2 className={cx("text-lg font-bold", "text-base font-bold")}>Your fleet is fully patched</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              No actively exploited vulnerabilities affect your managed devices. Keep monitoring for new releases.
            </p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <GlassCard variant="default" className="group">
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium">Open CVEs</p>
                <p className="text-3xl font-bold mt-1">{totalCVEs}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {fleetExposure.outdated > 0 ? `${fleetExposure.outdated} affect your fleet` : "none affect your fleet"}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                <Bug className="h-5 w-5 text-primary" />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard variant="default" className="group">
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium">Actively Exploited</p>
                <p className="text-3xl font-bold mt-1 text-destructive">{activelyExploitedCount}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {urgentDeviceCount > 0 ? `${urgentDeviceCount} device${urgentDeviceCount !== 1 ? "s" : ""} exposed` : "no devices exposed"}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-destructive/10 to-destructive/5 border border-destructive/20">
                <AlertTriangle className="h-5 w-5 text-destructive" />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard variant="default" className="group">
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium">Patches Available</p>
                <p className="text-3xl font-bold mt-1">{totalReleasesWithCVEs}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {criticalReleases} critical, {enrichedReleases.filter(r => r.priority === "high").length} high
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-[hsl(30_80%_55%)]/10 to-[hsl(30_80%_55%)]/5 border border-[hsl(30_80%_55%)]/20">
                <Shield className="h-5 w-5 text-[hsl(30_80%_55%)]" />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>

        <GlassCard variant="default" className="group">
          <GlassCardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground font-medium">Devices at Risk</p>
                <p className="text-3xl font-bold mt-1">{fleetExposure.outdated}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {fleetExposure.total > 0
                    ? `${Math.round((fleetExposure.outdated / fleetExposure.total) * 100)}% of fleet needs updates`
                    : "no devices enrolled"}
                </p>
              </div>
              <div className="p-3 rounded-xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20">
                <Monitor className="h-5 w-5 text-primary" />
              </div>
            </div>
          </GlassCardContent>
        </GlassCard>
      </div>

      {/* Recommended Actions */}
      <RecommendedActions actions={recommendedActions} />

      {/* What does this mean? */}
      <div className="flex items-start gap-3 rounded-xl border border-border/50 bg-muted/30 p-4">
        <Info className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
        <p className="text-xs text-muted-foreground leading-relaxed">
          This page shows known security vulnerabilities in Apple software that affect your managed devices.
          Items marked <strong className="text-foreground">Urgent</strong> are being actively exploited by attackers and should be patched immediately.
          Use the Recommended Actions above to prioritize your team's work.
        </p>
      </div>

      {/* Tabbed body: OS Releases / Applications / Remediation Policy */}
      <Tabs defaultValue="os" className="space-y-4">
        <TabsList>
          <TabsTrigger value="os">OS Releases</TabsTrigger>
          <TabsTrigger value="apps">Applications</TabsTrigger>
          <TabsTrigger value="policy">Remediation Policy</TabsTrigger>
        </TabsList>

        <TabsContent value="os" className="space-y-4">
          {/* Filters */}
          <div className={cx("backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl p-6 shadow-md", "backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-xl p-4 shadow-md")}>
            <VulnerabilityFilters
              timeRange={timeRange} onTimeRangeChange={setTimeRange}
              software={software} onSoftwareChange={setSoftware}
              severity={severity} onSeverityChange={setSeverity}
              searchQuery={searchQuery} onSearchChange={setSearchQuery}
            />
            <div className="flex items-center gap-2 mt-4">
              <Checkbox
                id="show-relevant"
                checked={showOnlyRelevant}
                onCheckedChange={(checked) => setShowOnlyRelevant(checked === true)}
              />
              <Label htmlFor="show-relevant" className="text-sm cursor-pointer">
                Show only releases affecting my fleet
                {devices.length > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {devices.length} devices
                  </Badge>
                )}
              </Label>
            </div>
          </div>

          {/* Releases Table */}
          <SecurityReleasesTable releases={filteredReleases} onCVEClick={handleCVEClick} />
        </TabsContent>

        <TabsContent value="apps">
          <ApplicationsVulnerabilityTab />
        </TabsContent>

        <TabsContent value="policy">
          <RemediationPolicyTab />
        </TabsContent>
      </Tabs>

      {/* Coverage charts (collapsible) */}
      <CollapsibleRoot>
        <div className="rounded-xl border border-border/50 bg-muted/20">
          <CollapsibleHead className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/40 transition-colors group [&[data-state=open]>svg]:rotate-180">
            <span>Coverage charts</span>
            <ChevronDownIcon className="h-4 w-4 text-muted-foreground transition-transform" />
          </CollapsibleHead>
          <CollapsibleBody>
            <div className="p-4 grid grid-cols-1 lg:grid-cols-3 gap-4">
              <GlassCard variant="default">
                <GlassCardHeader>
                  <GlassCardTitle className="text-sm font-medium">Fleet Exposure</GlassCardTitle>
                </GlassCardHeader>
                <GlassCardContent>
                  <div className="h-[250px]">
                    <FleetExposureChart exposure={fleetExposure} />
                  </div>
                </GlassCardContent>
              </GlassCard>

              <GlassCard variant="default">
                <GlassCardHeader>
                  <GlassCardTitle className="text-sm font-medium">CVEs by Platform</GlassCardTitle>
                </GlassCardHeader>
                <GlassCardContent>
                  <PlatformBreakdownChart releases={enrichedReleases} />
                </GlassCardContent>
              </GlassCard>

              <GlassCard variant="default">
                <GlassCardHeader>
                  <GlassCardTitle className="text-sm font-medium">CVEs by Release</GlassCardTitle>
                </GlassCardHeader>
                <GlassCardContent>
                  <div className="h-[250px]">
                    <ReleaseTimelineChart releases={enrichedReleases.filter(r => r.cveCount > 0)} />
                  </div>
                </GlassCardContent>
              </GlassCard>
            </div>
          </CollapsibleBody>
        </div>
      </CollapsibleRoot>

      {/* Detail Panel */}
      <VulnerabilityDetailPanel
        vulnerability={selectedVulnerability}
        open={detailPanelOpen}
        onOpenChange={setDetailPanelOpen}
      />
    </div>
  );
}

// --- Security Releases Table ---

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ChevronDown, ChevronRight, ExternalLink, ShieldAlert } from "lucide-react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

function SecurityReleasesTable({
  releases,
  onCVEClick,
}: {
  releases: NormalizedRelease[];
  onCVEClick: (cveId: string, release: NormalizedRelease) => void;
}) {
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = (id: string) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getPriorityBadge = (priority: string) => {
    switch (priority) {
      case "critical":
        return <Badge className="bg-destructive text-destructive-foreground">Critical</Badge>;
      case "high":
        return <Badge className="bg-[hsl(30_80%_55%)] text-white">High</Badge>;
      case "medium":
        return <Badge className="bg-[hsl(45_90%_55%)] text-black">Medium</Badge>;
      default:
        return <Badge variant="secondary">{priority}</Badge>;
    }
  };

  if (releases.length === 0) {
    return (
      <div className="text-center py-12 backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl shadow-lg">
        <ShieldAlert className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">No security releases match your filters.</p>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-md bg-white/40 dark:bg-slate-900/40 border border-white/10 dark:border-white/5 rounded-2xl shadow-lg overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead>Update</TableHead>
            <TableHead>Version</TableHead>
            <TableHead className="text-center">CVEs</TableHead>
            <TableHead className="text-center">Exploited</TableHead>
            <TableHead className="text-center">Impacted Devices</TableHead>
            <TableHead>Priority</TableHead>
            <TableHead>Released</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {releases.map(release => {
            const isExpanded = expandedRows.has(release.id);
            return (
              <Collapsible key={release.id} open={isExpanded} onOpenChange={() => toggleRow(release.id)} asChild>
                <>
                  <CollapsibleTrigger asChild>
                    <TableRow className="cursor-pointer hover:bg-muted/50 transition-colors">
                      <TableCell>
                        {release.cveCount > 0 && (
                          isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{release.updateName}</TableCell>
                      <TableCell className="font-mono text-sm">{release.productVersion}</TableCell>
                      <TableCell className="text-center">
                        <span className="font-semibold">{release.cveCount}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        {release.activelyExploitedCount > 0 ? (
                          <div className="flex items-center justify-center gap-1">
                            <AlertTriangle className="h-4 w-4 text-destructive" />
                            <span className="font-semibold text-destructive">{release.activelyExploitedCount}</span>
                          </div>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {(release.impactedDeviceCount || 0) > 0 ? (
                          <Badge variant="destructive">{release.impactedDeviceCount}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell>{getPriorityBadge(release.priority)}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {format(new Date(release.releaseDate), "MMM d, yyyy")}
                      </TableCell>
                    </TableRow>
                  </CollapsibleTrigger>
                  <CollapsibleContent asChild>
                    <TableRow className="bg-muted/20">
                      <TableCell colSpan={8} className="p-0">
                        <div className="px-6 py-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <p className="text-sm text-muted-foreground">{release.summary}</p>
                            <a
                              href={release.securityInfo}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline flex items-center gap-1"
                            >
                              Security Notes <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                          {release.cves.length > 0 && (
                            <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                              {release.cves.map(cve => (
                                <button
                                  key={cve.id}
                                  onClick={() => onCVEClick(cve.id, release)}
                                  className={`text-xs px-2 py-1 rounded-md border transition-colors cursor-pointer hover:bg-accent ${
                                    cve.detail.actively_exploited
                                      ? "border-destructive/50 bg-destructive/10 text-destructive font-semibold"
                                      : "border-border bg-background text-foreground"
                                  }`}
                                >
                                  {cve.id}
                                  {cve.detail.actively_exploited && " 🔥"}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  </CollapsibleContent>
                </>
              </Collapsible>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
